import React from 'react'
import { DateTime } from 'luxon'
import { Notification } from '~/models'
import { notificationsStore } from '~/stores'
import { memo } from '~/ui/component'
import { Chip, HBox, Label, ListItem } from '~/ui/components'
import { createUseStyles, layout } from '~/ui/styling'
import { Avatar } from '../media'
import { imageSize, isNotificationAvatar } from './NotificationPanel'

export interface Props {
  notification: Notification
}

const NotificationListItem = memo('NotificationListItem', (props: Props) => {

  const {notification} = props

  const markAsSeen = React.useCallback(
    () => notificationsStore.markAsSeen(notification.id),
    [notification.id],
  )

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <ListItem
        image={renderAvatar()}
        caption={notification.title ?? notificationsStore.defaultTitle}
        detail={[renderBody(), renderCreatedAt()]}
        href={notification.url}
        classNames={[$.NotificationListItem, {unseen: !notification.seen}]}
        onTap={markAsSeen}
        spacious
      />
    )
  }

  function renderBody() {
    const chips = (notification.labels?.map(label => (
      <Chip key={label} flex='none' small dim>{label}</Chip>
    )) ?? [])

    if (chips.length === 0) {
      return notification.seen ? notification.body : (
        <Label
          small
          children={notification.body}
        />
      )
    }

    return (
      <HBox classNames={$.labelledBody} gap={layout.padding.inline.s}>
        {chips}
        <Label
          small
          dim
          children={notification.body}
        />
      </HBox>
    )
  }

  function renderCreatedAt() {
    const minutes = Math.max(0, Math.floor(DateTime.now().diff(notification.createdAt, 'minutes').minutes))
    if (minutes >= 24 * 60) {
      return `${Math.floor(minutes / (24 * 60))}d`
    }
    if (minutes >= 60) {
      return `${Math.floor(minutes / 60)}u`
    }
    return `${minutes}m`
  }

  function renderAvatar() {
    if (!isNotificationAvatar(notification.avatar)) {
      return notification.avatar
    }

    return (
      <Avatar
        source={notification.avatar.photoURL}
        firstName={notification.avatar.firstName}
        lastName={notification.avatar.lastName}
        size={imageSize}
      />
    )
  }

  return render()

})

export default NotificationListItem

const useStyles = createUseStyles(theme => ({
  NotificationListItem: {
    '&.unseen': {
      backgroundColor: theme.semantic.primary.alpha(0.15),
    },
  },

  labelledBody: {
    minWidth: 0,
  },
}))